import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { reduceObjectByDefinedAttributes } from '../../utility.js';
import { getValidCurrentPage, getPageWithRange, getChangedPageSize, getItemRange, getTotalPages } from '../Pagination/utils.js';
import { getColumnRowHeader, getHasRowGroup, mapColumnsDef, updateColumnsDef } from './columnUtils.js';
import { INITIAL_STATE, SET_GROUP_CHILD_ROWS_ON_PAGE, SET_SELECTED_ROWS, SET_CURRENT_PAGE, SET_LAST_PAGE, SET_FIRST_PAGE, SET_PREV_PAGE, SET_NEXT_PAGE, SET_PAGE_SIZE, SET_SORTED_DISPLAY_DATA, SET_COLUMNS, SET_COLUMN, SET_CLIENT_PAGINATION, SET_SERVER_PAGINATION, SET_GENERAL_STATE, LAST, FIRST, PREV, NEXT } from './constants.js';
import { getRowSelection } from './rowUtils.js';
import { sortData } from './sortUtils.js';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

// Order rank of how state gets overwritten
// 1. incoming payload
// 2. existing state
// 3. default state

/**
 * tableReducer, Majority of global data manipulation happens here. Some of the row grouping data manipulation happens in the recursive rows.
 * @param {any} state
 * @param {any} action
 * @returns {any}
 */

var tableReducer = function tableReducer(state, action) {
  // The data table is set up to accept new dynamic data from the payload for the follow attributes
  var _action$payload = _objectSpread({}, action === null || action === void 0 ? void 0 : action.payload),
    alternateRowColor = _action$payload.alternateRowColor,
    cellPadding = _action$payload.cellPadding,
    className = _action$payload.className,
    dataTestId = _action$payload.dataTestId,
    data = _action$payload.data,
    fixedHeader = _action$payload.fixedHeader,
    groupDisplayType = _action$payload.groupDisplayType,
    height = _action$payload.height,
    name = _action$payload.name,
    onRowSelect = _action$payload.onRowSelect,
    onSortChange = _action$payload.onSortChange,
    pagination = _action$payload.pagination,
    roundCorner = _action$payload.roundCorner,
    rowSelection = _action$payload.rowSelection,
    selectedRows = _action$payload.selectedRows,
    scrollableWidth = _action$payload.scrollableWidth,
    _action$payload$showG = _action$payload.showGrid,
    showGrid = _action$payload$showG === void 0 ? INITIAL_STATE.showGrid : _action$payload$showG,
    sortableColumns = _action$payload.sortableColumns;
  var _INITIAL_STATE$pagina = _objectSpread(_objectSpread(_objectSpread({}, INITIAL_STATE.pagination), state === null || state === void 0 ? void 0 : state.pagination), reduceObjectByDefinedAttributes(pagination)),
    currentPage = _INITIAL_STATE$pagina.currentPage,
    hidePageSize = _INITIAL_STATE$pagina.hidePageSize,
    hidePaginationNavigation = _INITIAL_STATE$pagina.hidePaginationNavigation,
    hideShowItems = _INITIAL_STATE$pagina.hideShowItems,
    initialPageSize = _INITIAL_STATE$pagina.initialPageSize,
    location = _INITIAL_STATE$pagina.location,
    onPageSizeChange = _INITIAL_STATE$pagina.onPageSizeChange,
    onRequestFirst = _INITIAL_STATE$pagina.onRequestFirst,
    onRequestLast = _INITIAL_STATE$pagina.onRequestLast,
    onRequestPrev = _INITIAL_STATE$pagina.onRequestPrev,
    onRequestNext = _INITIAL_STATE$pagina.onRequestNext,
    onRequestPage = _INITIAL_STATE$pagina.onRequestPage,
    pageSelector = _INITIAL_STATE$pagina.pageSelector,
    pageSize = _INITIAL_STATE$pagina.pageSize,
    pageSizeOptions = _INITIAL_STATE$pagina.pageSizeOptions,
    showTopPagination = _INITIAL_STATE$pagina.showTopPagination,
    totalItems = _INITIAL_STATE$pagina.totalItems;
    _INITIAL_STATE$pagina.type;
  switch (action.type) {
    case SET_GENERAL_STATE:
      {
        var _data$columns, _state$columns;
        return _objectSpread(_objectSpread({}, state), {}, {
          alternateRowColor: alternateRowColor,
          cellPadding: cellPadding,
          className: className,
          dataTestId: dataTestId,
          displayData: sortData(data === null || data === void 0 ? void 0 : data.rowData, (data === null || data === void 0 ? void 0 : (_data$columns = data.columns) === null || _data$columns === void 0 ? void 0 : _data$columns.columnDefs) || (state === null || state === void 0 ? void 0 : (_state$columns = state.columns) === null || _state$columns === void 0 ? void 0 : _state$columns.columnDefs), data === null || data === void 0 ? void 0 : data.rowData, state === null || state === void 0 ? void 0 : state.isServer),
          fixedHeader: fixedHeader,
          groupDisplayType: groupDisplayType,
          height: height,
          onRowSelect: onRowSelect,
          onSortChange: onSortChange,
          // Todo check if name is being passed through
          name: name,
          rawData: (data === null || data === void 0 ? void 0 : data.rowData) || state.rawData,
          roundCorner: roundCorner,
          rowSelection: getRowSelection(rowSelection),
          scrollableWidth: scrollableWidth,
          selectedRows: selectedRows,
          showGrid: showGrid,
          sortableColumns: sortableColumns
        });
      }
    case SET_SERVER_PAGINATION:
      {
        var totalPages = getTotalPages(totalItems, pageSize);
        var shouldHidePagination = typeof totalItems !== 'number' || typeof currentPage !== 'number' || hidePageSize && hidePaginationNavigation && hideShowItems;

        // Show error when there are rows but current page is not set
        if (typeof currentPage !== 'number' && typeof totalItems === 'number') {
          console.error("Hey Canvas User! Server-side table pagination requires prop values for currentPage");
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          initialized: true,
          isServer: true,
          pagination: {
            currentPage: currentPage,
            hidePageSize: hidePageSize,
            hidePagination: shouldHidePagination,
            hidePaginationNavigation: hidePaginationNavigation,
            hideShowItems: hideShowItems,
            itemRange: getItemRange({
              page: currentPage,
              pageSize: pageSize,
              totalItems: totalItems,
              totalPages: totalPages
            }),
            location: location,
            onPageSizeChange: onPageSizeChange,
            onRequestNext: onRequestNext,
            onRequestPrev: onRequestPrev,
            onRequestFirst: onRequestFirst,
            onRequestLast: onRequestLast,
            onRequestPage: onRequestPage,
            pageSelector: pageSelector,
            pageSize: pageSize,
            pageSizeOptions: pageSizeOptions,
            showTopPagination: showTopPagination,
            totalItems: totalItems,
            totalPages: totalPages
          }
        });
      }
    case SET_CLIENT_PAGINATION:
      {
        var selectedPageSize = pageSize || initialPageSize || pageSizeOptions && pageSizeOptions[0] || !pageSizeOptions && INITIAL_STATE.pagination.pageSizeOptions[0];
        var _totalPages = getTotalPages(totalItems, selectedPageSize);
        var validCurrentPage = getValidCurrentPage(currentPage, _totalPages);
        var _shouldHidePagination = typeof totalItems !== 'number' || hidePageSize && hidePaginationNavigation && hideShowItems;
        return _objectSpread(_objectSpread({}, state), {}, {
          isClient: true,
          pagination: _objectSpread(_objectSpread({}, state.pagination), {}, {
            onPageSizeChange: onPageSizeChange,
            onRequestNext: onRequestNext,
            onRequestPrev: onRequestPrev,
            onRequestFirst: onRequestFirst,
            onRequestLast: onRequestLast,
            onRequestPage: onRequestPage,
            currentPage: validCurrentPage,
            hidePageSize: hidePageSize,
            hidePagination: _shouldHidePagination,
            hidePaginationNavigation: hidePaginationNavigation,
            hideShowItems: hideShowItems,
            location: location,
            pageSelector: pageSelector,
            pageSize: selectedPageSize,
            pageSizeOptions: pageSizeOptions,
            totalItems: totalItems,
            totalPages: _totalPages
          }, getPageWithRange(_objectSpread(_objectSpread({}, state.pagination), {}, {
            currentPage: validCurrentPage,
            pageSize: selectedPageSize,
            totalItems: totalItems,
            totalPages: _totalPages
          })))
        });
      }
    case SET_COLUMN:
      {
        var columnDefs = updateColumnsDef({
          columnDefs: state.columns.columnDefs,
          field: action.payload.field,
          sortDirection: action.payload.sortDirection,
          sortType: action.payload.sortType,
          width: action.payload.width
        });
        return _objectSpread(_objectSpread({}, state), {}, {
          columns: _objectSpread(_objectSpread({}, state.columns), {}, {
            columnDefs: columnDefs
          })
        });
      }
    case SET_COLUMNS:
      {
        var rowHeader = getColumnRowHeader(data === null || data === void 0 ? void 0 : data.columnDefs);
        var hasRowGroup = getHasRowGroup(data === null || data === void 0 ? void 0 : data.columnDefs);
        var _columnDefs = mapColumnsDef(data === null || data === void 0 ? void 0 : data.columnDefs, state.columns.columnDefs, data === null || data === void 0 ? void 0 : data.defaultColDef, rowHeader === null || rowHeader === void 0 ? void 0 : rowHeader.field);
        return _objectSpread(_objectSpread({}, state), {}, {
          columns: {
            columnDefs: _columnDefs,
            hasRowGroup: hasRowGroup
          }
        });
      }
    case SET_SORTED_DISPLAY_DATA:
      {
        var sortedData = sortData(state.rawData, state.columns.columnDefs, state.rawData, state === null || state === void 0 ? void 0 : state.isServer);
        if (typeof (state === null || state === void 0 ? void 0 : state.onSortChange) === 'function') {
          state.onSortChange(sortedData);
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          displayData: sortedData
        });
      }
    case SET_PAGE_SIZE:
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          pagination: _objectSpread(_objectSpread({}, state.pagination), getChangedPageSize(state.pagination, Number(action.payload)))
        });
      }
    case SET_NEXT_PAGE:
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          pagination: _objectSpread(_objectSpread({}, state.pagination), getPageWithRange(state.pagination, NEXT))
        });
      }
    case SET_PREV_PAGE:
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          pagination: _objectSpread(_objectSpread({}, state.pagination), getPageWithRange(state.pagination, PREV))
        });
      }
    case SET_FIRST_PAGE:
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          pagination: _objectSpread(_objectSpread({}, state.pagination), getPageWithRange(state.pagination, FIRST))
        });
      }
    case SET_LAST_PAGE:
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          pagination: _objectSpread(_objectSpread({}, state.pagination), getPageWithRange(state.pagination, LAST))
        });
      }
    case SET_CURRENT_PAGE:
      {
        var newPage = getValidCurrentPage(Number(action.payload), state.pagination.totalPages);
        return _objectSpread(_objectSpread({}, state), {}, {
          pagination: _objectSpread(_objectSpread({}, state.pagination), {}, {
            currentPage: newPage
          }, getPageWithRange(_objectSpread(_objectSpread({}, state.pagination), {}, {
            currentPage: newPage
          })))
        });
      }
    case SET_SELECTED_ROWS:
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          selectedRows: action.payload.selectedRows
        });
      }
    case SET_GROUP_CHILD_ROWS_ON_PAGE:
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          groupChildRowsOnPage: action.payload.groupChildRowsOnPage
        });
      }
    default:
      return state;
  }
};

export { tableReducer as default };
